import styled from '@emotion/styled';
import { Spin, Table } from 'antd';
import LegendAlt from 'components/molecules/LegendAlt';
import ScorecardExpandedView from 'components/template/PromoScorecard/ScorecardExpandedView';
import { colorConstants } from 'config/colorConstants';
import React, { useEffect, useMemo, useState, useContext, useRef, useCallback } from 'react';
import tw from 'twin.macro';
import { getScorecardQuestionsColumns } from 'utils/getScorecardColumns';
import { getScoreCardQuestionsTableData } from 'utils/testData';
import { useFetchAllQuestions } from 'hooks/promoScoreCard/useFetchAllQuestions';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import {
    convertGlobalFilterToHtml,
    formatItemsOnCollapseState,
    formatValue,
    getAdditionalDataOnCopyClick,
    getConfig,
    globalFilterFlattened,
    parseAttributeType,
} from 'utils/helperMethods';
import ShowSelection from 'components/molecules/ShowSelection';
import Legend from 'components/molecules/Legend';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PROMO_SCORECARD_QUESTIONS_COPY,
    PROMO_SCORECARD_QUESTIONS_EXPORT,
    PROMO_SCORECARD_QUESTION_CLICKED,
} from 'utils/mixpanelEventNames';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import PromoScorecardSelection from 'components/molecules/PromoScorecard/PromoScorecardSelection';
import _ from 'lodash';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { promoScoreCardTableParser } from 'parser/graphToExcelDownloadParser';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';

export const scoreHeaderClass = 'text-greyFont text-[15px] font-normal';
export const commonClassName = 'border  border-borderLinePurpuleAlt p-4 rounded-lg';

const rowKey = 'rowKey';

const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
    }

    // .ant-table-cell-scrollbar {
    //     display: none !important;
    // }
    .bold {
        font-weight: bold;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border-radius: 4px;
    ${tw`border border-borderLinePurpuleAlt`}
`;

const dummyVariables = {
    filtersC: '{"dim1":{"ids":[],"__typename":"ApplyFilter"},"dim2":{"ids":[],"__typename":"ApplyFilter"}}',
};

export const commonHeaderClass = 'font-medium font-xl m-0 text-headerColor';

const questionsTableColorData = [
    { label: 'Positive', colors: [colorConstants.lightGreen, colorConstants.limeGreen] },
    { label: 'Neutral', colors: [colorConstants.white, colorConstants.greyAlt] },
    { label: 'Negative', colors: [colorConstants.lightRed, colorConstants.red] },
];

const PromoScorecard = () => {
    const { appConfig } = useContext(UserContext);
    const promoScoreCardConfig = getConfig('promoScoreCard', appConfig);
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
    const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number | null>(null);
    const [selectedAttributeName, setSelectedAttributeName] = useState<string>(promoScoreCardConfig.dropDown[1]);
    const [loadingStateForFilterC, setLoadingStateForFilterC] = useState(false);
    const [loadingStateForAttrNames, setLoadingStateForAttrNames] = useState(false);
    const prevIds = useRef<any>({
        idForRestOfComponent: null,
        idForRestOfComponentFilterB: null,
        filterC: null,
    });
    const [attributeTypesList, setAttributeTypesList] = useState<any[]>([]);
    const [selectedAttributeType, setSelectedAttributeType] = useState(
        attributeTypesList.length ? attributeTypesList[5] : null,
    );

    const scoreCardRef = useRef<any>();
    const [filterC, setFilterC] = useState<any>(null);
    const tableOnClickHandler = useCallback(
        (record: any, index: number) => {
            if (selectedQuestionIndex !== index) {
                setSelectedQuestionIndex(index);
                //mixpanel event
                trackEventMixpanel(PROMO_SCORECARD_QUESTION_CLICKED, {
                    question: record?.QuestionsData,
                    isActive: true,
                });
            } else {
                setSelectedQuestionIndex(null);
                //mixpanel event
                trackEventMixpanel(PROMO_SCORECARD_QUESTION_CLICKED, {
                    question: record?.QuestionsData,
                    isActive: false,
                });
            }
        },
        [selectedQuestionIndex, setSelectedQuestionIndex],
    );
    const {
        timePeriodSelection,
        idForRestOfComponent,
        allCheckedAttribute,
        idForRestOfComponentLoading,
        filterNullError,
        idForRestOfComponentFilterB,
        loadingForAllCheckAttribute,
        timeSelectionLabel,
        allDimAndAttributeTypeList,
        screenFilterConfig,
        filterBLoading,
    } = useContext(FilterContextData);

    const { loading: debouncedFilterLoading } = useDebounceLoading({ loadingDependency: filterBLoading });
    const { allQuestionAndLoading, promoScoreCardTable, fetchAllQuestions } = useFetchAllQuestions();
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            filterC &&
            !debouncedFilterLoading
        ) {
            let JSONFilterA = JSON.stringify(idForRestOfComponent);
            let JSONFilterB = JSON.stringify(idForRestOfComponentFilterB);
            let JSONFilterC = JSON.stringify(filterC);
            fetchAllQuestions({
                ...dummyVariables,
                filtersA: JSONFilterA,
                filtersB: JSONFilterB,
                filtersC: JSONFilterC,
                timePeriod: String(timePeriodSelection).toLowerCase(),
                offset: 0,
                limit: promoScoreCardConfig.nestedTableLimit,
                attributeType: _.snakeCase(selectedAttributeName),
                aggregatedItem: formatItemsOnCollapseState(allCheckedAttribute, _.snakeCase(selectedAttributeName), 1),
            });
            // prevIds.current.idForRestOfComponent = idForRestOfComponent;
            // prevIds.current.idForRestOfComponentFilterB = idForRestOfComponentFilterB;
            // prevIds.current.filterC = JSONFilterC;
        }
    }, [timePeriodSelection, allDimAndAttributeTypeList?.loading, filterC, debouncedFilterLoading]);

    const commonLoading = debouncedFilterLoading || loadingStateForFilterC;
    //fetching 2nd dropdown values
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !selectedAttributeType
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions']);
            if (data.length) {
                data = data.filter((item: any) => item.dimension === 'dim1');
                setAttributeTypesList(data);
                setSelectedAttributeType(data[5]); //sub brand
            }
        }

        //cleanup on global filter change
        return () => {
            setAttributeTypesList([]);
            setSelectedAttributeType(null);
        };
    }, [allDimAndAttributeTypeList?.loading]);

    const questionObj: any = useMemo(() => {
        if (selectedQuestionIndex !== null) return promoScoreCardTable?.[selectedQuestionIndex];
    }, [selectedQuestionIndex]);

    const memoisedTableColumns = useMemo(() => {
        return getScorecardQuestionsColumns(
            getScoreCardQuestionsTableData(appConfig),
            tableOnClickHandler,
            promoScoreCardConfig.promoType.length,
            selectedDropdownIndex,
            setSelectedDropdownIndex,
            _.startCase(selectedAttributeName),
            questionObj,
            promoScoreCardConfig.showGradientTile,
        );
    }, [tableOnClickHandler, selectedDropdownIndex, promoScoreCardTable, selectedAttributeName, questionObj]); //use usecallback for function tableonclickhandler

    const tableExcelData = useMemo(() => {
        return promoScoreCardTableParser(
            promoScoreCardTable,
            getScoreCardQuestionsTableData(appConfig),
            _.startCase(selectedAttributeName),
        );
    }, [promoScoreCardTable]);

    return (
        <section className={` ${commonClassName} bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div>
                    <h2 className={commonHeaderClass}>{promoScoreCardConfig?.mainHeader}</h2>
                    {false ? (
                        <div className="flex justify-start items-center h-20 ml-8">
                            <Spin />
                        </div>
                    ) : (
                        <div className="flex my-4 justify-between">
                            {loadingForAllCheckAttribute ? (
                                <div className="flex w-full justify-center">
                                    <Spin />
                                </div>
                            ) : (
                                <div className="flex gap-2 flex-col">
                                    <ShowSelection
                                        allCheckedAttribute={allCheckedAttribute}
                                        listOfAttribute={[...promoScoreCardConfig.listForToDisplay]}
                                    />
                                    <ShowSelection
                                        allCheckedAttribute={allCheckedAttribute}
                                        title={`Score in :`}
                                        listOfAttribute={promoScoreCardConfig.listIn}
                                    />
                                </div>
                            )}
                            <div className="flex items-end">
                                {promoScoreCardConfig?.showGradientTile && <LegendAlt data={questionsTableColorData} />}
                            </div>
                        </div>
                    )}
                    <div className="flex justify-between items-center">
                        <PromoScorecardSelection
                            screenFilterConfig={screenFilterConfig}
                            selectedAttributeName={selectedAttributeName}
                            setSelectedAttributeName={setSelectedAttributeName}
                            idForRestOfComponentFilterB={idForRestOfComponentFilterB}
                            setFilterC={setFilterC}
                            setLoadingStateForFilterC={setLoadingStateForFilterC}
                            setLoadingStateForAttrNames={setLoadingStateForAttrNames}
                            promoScoreCardConfig={promoScoreCardConfig}
                        />
                    </div>
                    <span className="flex gap-2 w-full justify-end mb-2">
                        <CopyToClipBoard
                            refOfComponent={scoreCardRef}
                            mixpanelEvent={() => trackEventMixpanel(PROMO_SCORECARD_QUESTIONS_COPY)}
                        />
                        <DownloadExcel
                            mixpanelEvent={() => trackEventMixpanel(PROMO_SCORECARD_QUESTIONS_EXPORT)}
                            title={'PromoScoreCard'}
                            excelData={tableExcelData?.result}
                            headers={tableExcelData?.header}
                        />
                    </span>
                    {commonLoading ? (
                        <div className="flex justify-center items-center h-20 w-full">
                            <Spin />
                        </div>
                    ) : (
                        <div ref={scoreCardRef}>
                            <CustomTable
                                //bordered
                                pagination={false}
                                scroll={{ x: '100%', y: 500 }}
                                sticky
                                rowClassName={(record: any, index) => {
                                    return record?.QuestionIndex === questionObj?.QuestionIndex ? 'bold' : '';
                                }}
                                rowKey={(record: any) => record[rowKey]}
                                columns={memoisedTableColumns}
                                dataSource={promoScoreCardTable}
                                // loading={tableLoading}
                            />
                        </div>
                    )}
                    {selectedQuestionIndex !== null && (
                        <div>
                            <ScorecardExpandedView
                                attributeTypeList={attributeTypesList}
                                selectedAttributeType={selectedAttributeType}
                                setSelectedAttributeType={setSelectedAttributeType}
                                questionObj={questionObj}
                                indexOfClicked={selectedQuestionIndex}
                                showGradientTile={promoScoreCardConfig.showGradientTile}
                            />
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default PromoScorecard;
